import React, { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { getStats, getResponses } from "../adminApi";
import { KpiCard } from "../components/KpiCard";
import { LineChart } from "../components/LineChart";
import { BarChart } from "../components/BarChart";
import { Pill } from "../components/Pill";
import {
    INDIVIDUAL_PRICE_LABELS,
    INDIVIDUAL_PRICE_ORDER,
} from "../labels";

export default function AdminDashboard() {
    const { includeSeed, refreshKey } = useOutletContext();
    const [stats, setStats] = useState(null);
    const [recent, setRecent] = useState([]);
    const [loading, setLoading] = useState(true);
    const [err, setErr] = useState(null);

    useEffect(() => {
        let alive = true;
        setLoading(true);
        setErr(null);
        Promise.all([
            getStats(includeSeed),
            getResponses({
                page: 1,
                limit: 8,
                include_seed: includeSeed,
                has_email: undefined,
                search: undefined,
            }),
        ])
            .then(([statsData, list]) => {
                if (!alive) return;
                setStats(statsData);
                setRecent(
                    (list.items || []).filter((r) => (r.one_thing || "").trim().length > 0),
                );
            })
            .catch((e) => alive && setErr(e?.message || "Failed to load"))
            .finally(() => alive && setLoading(false));
        return () => {
            alive = false;
        };
    }, [includeSeed, refreshKey]);

    if (loading) {
        return (
            <div className="grid place-items-center py-20">
                <div className="inline-block w-8 h-8 rounded-full border-2 border-klareet-bor border-t-klareet-T animate-spin" />
            </div>
        );
    }

    if (err || !stats) {
        return (
            <div className="rounded-2xl border border-klareet-RED/40 bg-klareet-RED/5 p-6 text-klareet-RED">
                {err || "No data."}
            </div>
        );
    }

    const personaData = Object.entries(stats.persona_breakdown).map(
        ([label, value]) => ({ label, value }),
    );
    const painData = Object.entries(stats.pain_breakdown).map(
        ([label, value]) => ({ label, value }),
    );
    const priceBreakdown = stats.price_individual_breakdown || {};
    const priceData = INDIVIDUAL_PRICE_ORDER.filter(
        (k) => priceBreakdown[k] != null,
    ).map((k) => ({
        label: INDIVIDUAL_PRICE_LABELS[k] || k,
        value: priceBreakdown[k] || 0,
    }));
    const waitlistData = Object.entries(stats.waitlist_breakdown).map(
        ([label, value]) => ({ label, value }),
    );

    return (
        <div className="space-y-8" data-testid="admin-dashboard">
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 md:gap-4">
                <KpiCard
                    label="Total responses"
                    value={stats.total}
                    testId="kpi-total"
                />
                <KpiCard
                    label="Last 24h"
                    value={stats.today}
                    testId="kpi-today"
                />
                <KpiCard
                    label="Avg appeal"
                    value={stats.avg_appeal.toFixed(1)}
                    hint="1.0 – 10.0"
                    testId="kpi-appeal"
                />
                <KpiCard
                    label="With email"
                    value={`${stats.with_email_pct}%`}
                    testId="kpi-email"
                />
                <KpiCard
                    label="Waitlist yes"
                    value={`${stats.waitlist_yes_pct}%`}
                    hint="paid + free"
                    testId="kpi-waitlist"
                />
                <KpiCard
                    label="Free-year earners"
                    value={stats.free_year_earners}
                    testId="kpi-earners"
                />
            </div>

            <LineChart
                series={stats.responses_per_day_last_30}
                title="Responses per day · last 30"
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <BarChart title="Persona" data={personaData} total={stats.total} />
                <BarChart title="Pain" data={painData} total={stats.total} />
                <BarChart
                    title="Pricing (individual)"
                    data={priceData}
                    total={priceData.reduce((a, b) => a + b.value, 0)}
                    preserveCase
                />
                <BarChart
                    title="Waitlist intent"
                    data={waitlistData}
                    total={stats.total}
                />
            </div>

            <div className="rounded-2xl border border-klareet-bor bg-klareet-card p-6">
                <div className="flex items-center justify-between mb-4">
                    <div className="text-[11px] uppercase tracking-[0.2em] font-mono text-klareet-MUT">
                        Recent open answers
                    </div>
                    <Link
                        to="/admin/responses"
                        className="inline-flex items-center gap-1 text-sm text-klareet-T2 hover:text-klareet-TEXT transition-colors"
                        data-testid="view-all-responses-link"
                    >
                        View all
                        <ArrowRight size={14} />
                    </Link>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {recent.length === 0 ? (
                        <div className="text-sm text-klareet-MUT col-span-2">
                            No open answers yet.
                        </div>
                    ) : (
                        recent.slice(0, 8).map((r) => (
                            <div
                                key={r.id}
                                className="rounded-2xl border border-klareet-bor bg-klareet-NAVY/40 p-4"
                            >
                                <div className="flex items-center gap-2 mb-2">
                                    <Pill value={r.persona} kind="persona" />
                                    <Pill value={r.pain} kind="pain" />
                                </div>
                                <p className="text-sm text-klareet-TEXT/90 italic leading-relaxed line-clamp-4">
                                    “{r.one_thing}”
                                </p>
                                {r.name ? (
                                    <div className="mt-2 text-xs text-klareet-DIM font-mono">
                                        — {r.name}
                                    </div>
                                ) : null}
                            </div>
                        ))
                    )}
                </div>
            </div>
        </div>
    );
}
